import { useState } from "react";
import {
  Box, Typography, Paper, Grid,
  Button, Table, TableHead, TableRow,
  TableCell, TableBody, CircularProgress,
  Alert, Dialog, TextField, Collapse, IconButton, Chip
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

import api from "../service/api";
import { salesReportService } from "../service/SalesReportService";

const SalesReport = () => {

  // 🔹 STATES
  const [mode, setMode] = useState("monthly");
  const [date, setDate] = useState("");
  const [year, setYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(null);

  const [tableData, setTableData] = useState({});
  const [weekList, setWeekList] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [openView, setOpenView] = useState(false);
  const [viewData, setViewData] = useState(null);

  // 🔹 Collapse state per location
  const [collapsed, setCollapsed] = useState({});

  const toggleLocation = (loc) =>
    setCollapsed((p) => ({ ...p, [loc]: !p[loc] }));

  // 🔥 GROUP BY LOCATION
  const groupByLocation = (sales = []) => {
    const grouped = {};
    sales.forEach((s) => {
      if (!grouped[s.location]) grouped[s.location] = [];
      grouped[s.location].push(s);
    });
    setCollapsed({});
    return grouped;
  };

  // 📅 DAILY
  const handleDaily = async () => {
    try {
      setLoading(true);
      const res = await salesReportService.getByDate(date);
      const sales = [
        ...(res.data?.cash?.sales     || []),
        ...(res.data?.finance?.sales  || []),
        ...(res.data?.exchange?.sales || [])
      ];
      setTableData(groupByLocation(sales));
      setWeekList([]);
    } catch {
      setError("Daily sales failed");
    } finally {
      setLoading(false);
    }
  };

  // 📊 WEEKLY
  const handleWeekly = async () => {
    try {
      setLoading(true);
      const res = await salesReportService.getWeekly(year);

      // API returns { data: { cash: [...weeks], finance: [...weeks], exchange: [...weeks] } }
      // Each week has: { week, totalSold, totalRevenue, totalDue, sales[] }
      // Merge all three arrays into one list keyed by week number
      const cashWeeks     = res.data?.cash     || [];
      const financeWeeks  = res.data?.finance  || [];
      const exchangeWeeks = res.data?.exchange || [];

      const weekMap = {};
      [...cashWeeks, ...financeWeeks, ...exchangeWeeks].forEach((w) => {
        if (!weekMap[w.week]) {
          weekMap[w.week] = {
            week:         w.week,
            totalSold:    0,
            totalRevenue: 0,
            totalDue:     0,
            sales:        [],
          };
        }
        weekMap[w.week].totalSold    += w.totalSold    || 0;
        weekMap[w.week].totalRevenue += w.totalRevenue || 0;
        weekMap[w.week].totalDue     += w.totalDue     || 0;
        weekMap[w.week].sales         = [...weekMap[w.week].sales, ...(w.sales || [])];
      });

      // Sort ascending by week number
      const merged = Object.values(weekMap).sort((a, b) => a.week - b.week);
      setWeekList(merged);
      setTableData({});
    } catch {
      setError("Weekly failed");
    } finally {
      setLoading(false);
    }
  };

  const handleWeekClick = (week) => {
    setTableData(groupByLocation(week.sales || []));
  };

  // 📈 MONTHLY
  const handleMonthClick = async (month) => {
    try {
      setLoading(true);
      setSelectedMonth(month);
      const res = await salesReportService.getMonthly(year, month);
      const sales = [
        ...(res.data?.cash?.sales     || []),
        ...(res.data?.finance?.sales  || []),
        ...(res.data?.exchange?.sales || [])
      ];
      setTableData(groupByLocation(sales));
      setWeekList([]);
    } catch {
      setError("Monthly failed");
    } finally {
      setLoading(false);
    }
  };

  // 👁 VIEW DETAILS
  const handleView = async (id) => {
    const res = await api.get(`/specific-sold-bike-details/${id}`);
    setViewData(res.data.data);
    setOpenView(true);
  };

  return (
    <Box p={{ xs: 1, md: 3 }}>

      <Typography variant="h5" fontWeight={700}>Sales Report</Typography>

      {/* 🔥 MODE SWITCH */}
      <Paper sx={{ p: 2, mt: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item>
            <Button variant={mode === "daily"   ? "contained" : "outlined"} onClick={() => setMode("daily")}>Daily</Button>
          </Grid>
          <Grid item>
            <Button variant={mode === "weekly"  ? "contained" : "outlined"} onClick={() => setMode("weekly")}>Weekly</Button>
          </Grid>
          <Grid item>
            <Button variant={mode === "monthly" ? "contained" : "outlined"} onClick={() => setMode("monthly")}>Monthly</Button>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField label="Year" size="small" fullWidth value={year} onChange={(e) => setYear(e.target.value)} />
          </Grid>
        </Grid>
      </Paper>

      {/* 📅 DAILY */}
      {mode === "daily" && (
        <Paper sx={{ p: 2, mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField type="date" fullWidth value={date} onChange={(e) => setDate(e.target.value)} />
            </Grid>
            <Grid item>
              <Button variant="contained" onClick={handleDaily}>Load</Button>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* 📊 WEEKLY */}
      {mode === "weekly" && (
        <Paper sx={{ p: 2, mt: 2 }}>
          <Button variant="contained" onClick={handleWeekly}>Load Weekly</Button>
          <Grid container spacing={1} mt={1}>
            {weekList.map((w) => (
              <Grid item key={w.week} xs={6} sm={4} md={3}>
                <Button
                  fullWidth variant="outlined"
                  onClick={() => handleWeekClick(w)}
                  sx={{ display: "flex", flexDirection: "column", py: 1, textTransform: "none" }}
                >
                  <Typography fontWeight={700} fontSize="0.85rem">Week {w.week}</Typography>
                  <Typography fontSize="0.7rem" color="text.secondary">
                    {w.totalSold} sales · ₹{Number(w.totalRevenue).toLocaleString("en-IN")}
                  </Typography>
                </Button>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      {/* 📈 MONTHLY */}
      {mode === "monthly" && (
        <Paper sx={{ p: 2, mt: 2 }}>
          <Grid container spacing={1}>
            {Array.from({ length: 12 }, (_, i) => (
              <Grid item xs={4} sm={3} md={2} key={i}>
                <Button
                  fullWidth
                  variant={selectedMonth === i + 1 ? "contained" : "outlined"}
                  onClick={() => handleMonthClick(i + 1)}
                >
                  {i + 1}
                </Button>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      {loading && <CircularProgress sx={{ mt: 2 }} />}
      {error   && <Alert severity="error">{error}</Alert>}

      {/* 🔥 EXPAND ALL / COLLAPSE ALL */}
      {Object.keys(tableData).length > 0 && (
        <Box display="flex" gap={1} mt={2}>
          <Button size="small" variant="outlined"
            onClick={() => setCollapsed(Object.fromEntries(Object.keys(tableData).map((k) => [k, false])))}>
            Expand All
          </Button>
          <Button size="small" variant="outlined"
            onClick={() => setCollapsed(Object.fromEntries(Object.keys(tableData).map((k) => [k, true])))}>
            Collapse All
          </Button>
        </Box>
      )}

      {/* 📍 TABLE — collapsible per location */}
      {Object.keys(tableData).map((loc) => {
        const isOpen = !collapsed[loc];
        return (
          <Paper sx={{ mt: 2, overflow: "hidden" }} key={loc}>

            {/* ── Location Header ── */}
            <Box
              onClick={() => toggleLocation(loc)}
              sx={{
                px: 2, py: 1.5, cursor: "pointer",
                bgcolor: "#f0f4ff",
                borderBottom: isOpen ? "1px solid #e0e0e0" : "none",
                display: "flex", alignItems: "center", justifyContent: "space-between"
              }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <Typography fontWeight={700}>📍 {loc}</Typography>
                <Chip
                  size="small"
                  label={`${tableData[loc].length} sales`}
                  sx={{ bgcolor: "#1565c0", color: "#fff", fontWeight: 700, fontSize: "0.7rem" }}
                />
              </Box>
              <IconButton size="small">
                {isOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </IconButton>
            </Box>

            {/* ── Collapsible Table ── */}
            <Collapse in={isOpen}>
              <Box sx={{ overflowX: "auto" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: "#fafafa" }}>
                      <TableCell sx={{ fontWeight: 700 }}>Model</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Customer</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Price</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tableData[loc].map((s) => (
                      <TableRow key={s._id} sx={{ "&:hover": { bgcolor: "#f5f8ff" } }}>
                        <TableCell>{s.model}</TableCell>
                        <TableCell>{s.customerName}</TableCell>
                        <TableCell>₹{s.salePrice}</TableCell>
                        <TableCell>{s.saleType}</TableCell>
                        <TableCell>{new Date(s.saleDate).toLocaleDateString()}</TableCell>
                        <TableCell>
                          <Button size="small" variant="contained" onClick={() => handleView(s._id)}>
                            VIEW
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>

          </Paper>
        );
      })}

      {/* 🔥 VIEW DIALOG — unchanged */}
      <Dialog open={openView} onClose={() => setOpenView(false)} fullWidth maxWidth="md">
        <Box p={3} sx={{ background: "#f8fafc" }}>
          <Typography variant="h6" fontWeight={700} mb={2}>🧾 Sale Full Details</Typography>

          {!viewData ? (
            <Alert severity="info">No data found</Alert>
          ) : (
            <Grid container spacing={2}>

              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography fontWeight={700} mb={1}>🚗 Bike Info</Typography>
                  <Typography><b>Model:</b> {viewData.model}</Typography>
                  <Typography><b>Engine:</b> {viewData.engineNumber}</Typography>
                  <Typography><b>Chasis:</b> {viewData.chasisNumber}</Typography>
                  <Typography><b>Color:</b> {viewData.color}</Typography>
                  <Typography><b>Location:</b> {viewData.location}</Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography fontWeight={700} mb={1}>👤 Customer</Typography>
                  <Typography><b>Name:</b> {viewData.customerName}</Typography>
                  <Typography><b>Mobile:</b> {viewData.mobile?.[0]}</Typography>
                  <Typography><b>Address:</b> {viewData.address}</Typography>
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Paper sx={{ p: 2 }}>
                  <Typography fontWeight={700} mb={1}>💰 Sale Info</Typography>
                  <Typography><b>Price:</b> ₹{viewData.salePrice}</Typography>
                  <Typography><b>Type:</b> {viewData.saleType}</Typography>
                  <Typography><b>Date:</b> {new Date(viewData.saleDate).toLocaleString()}</Typography>
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Paper sx={{ p: 2 }}>
                  <Typography fontWeight={700} mb={1}>💸 Due Info</Typography>
                  <Typography><b>Total Due:</b> ₹{viewData.due?.totalDue}</Typography>
                  <Typography><b>Status:</b> {viewData.due?.status}</Typography>
                  {viewData.due?.payments?.length > 0 ? (
                    viewData.due.payments.map((p, i) => (
                      <Typography key={i}>₹{p.amount} on {new Date(p.date).toLocaleDateString()}</Typography>
                    ))
                  ) : (
                    <Typography>No due payments</Typography>
                  )}
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Paper sx={{ p: 2 }}>
                  <Typography fontWeight={700} mb={1}>👨‍💼 Sold By</Typography>
                  <Typography>{viewData.soldBy?.name}</Typography>
                  <Typography>{viewData.soldBy?.email}</Typography>
                </Paper>
              </Grid>

            </Grid>
          )}
        </Box>
      </Dialog>

    </Box>
  );
};

export default SalesReport;